import type { PaletteDef, PlanetDef } from "@hyperlight/shared";
import { hasLivingGround } from "./groundSurface";

type RGB = [number, number, number];
interface LandscapeColors { ground: PaletteDef; foliage: RGB; rock: RGB }

// Linear albedo: warm dry earth, olive meadow, deep woodland, cool mineral.
// Rows retain the production cold→hot / dry→wet interpolation contract.
const LIVING_TABLE: RGB[] = [
  [.29, .28, .22], [.15, .22, .14], [.11, .18, .13],
  [.36, .31, .17], [.24, .35, .12], [.14, .25, .09],
  [.43, .34, .17], [.29, .40, .13], [.10, .21, .08],
  [.56, .44, .26], [.29, .32, .12], [.08, .17, .09],
];
const blend = (a: RGB, b: RGB, k: number): RGB =>
  [a[0] + (b[0] - a[0]) * k, a[1] + (b[1] - a[1]) * k, a[2] + (b[2] - a[2]) * k];
const cache = new WeakMap<PlanetDef, LandscapeColors>();

/** Presentation palette only. Never mutate the generated definition or consume
 * its RNG: physical terrain, climate, settlement records and saved seeds stay
 * byte-identical. Consumers share this cached palette at every render distance.
 * Loose stone shares exposed terrain's mineral family on every archetype. */
export function landscapeColors(def: PlanetDef): LandscapeColors {
  let colors = cache.get(def);
  if (colors) return colors;
  if (hasLivingGround(def)) {
    const ground: PaletteDef = {
      ...def.palette,
      table: LIVING_TABLE.map((c, i) => blend(c, def.palette.table[i], .18)),
      cliff: blend([.20, .245, .31], def.palette.cliff, .18),
      sand: blend([.61, .50, .33], def.palette.sand, .18),
    };
    colors = { ground, foliage: blend(ground.table[5], ground.table[8], .4), rock: ground.cliff };
  } else {
    const sc = def.scatterColor;
    colors = { ground: def.palette, foliage: sc, rock: def.palette.cliff };
  }
  cache.set(def, colors);
  return colors;
}

export function landscapePalette(def: PlanetDef): PaletteDef { return landscapeColors(def).ground; }
